import { useEffect } from "react";
import { shallow } from "zustand/shallow";
import { useAccessControl, type AccessControl } from "@/hooks/useAccessControl";
import useTabStore from "@/stores/useTabStore";
import { useOnboardingStore } from "./useOnboardingStore";

/**
 * Dev/testing override — NON-PRODUCTION BUILDS ONLY. Manufacturing a real
 * trialist (Chargebee trial synced to in_trial + empty server tabs) is
 * heavyweight; these query params let any authenticated account drive the
 * flow on dev/preview:
 *
 *   ?onboarding=force  — treat this account as an eligible fresh trialist
 *                        (skips the in_trial and empty-tabs checks)
 *   ?onboarding=reset  — wipe saved onboarding state server-side, then
 *                        reload into force mode: the welcome flow re-runs
 *                        from scratch every time
 */
export type OnboardingDevOverride = "force" | "reset" | null;

export function getOnboardingDevOverride(): OnboardingDevOverride {
  if (import.meta.env.VITE_APP_ENV === "production") return null;
  const value = new URLSearchParams(window.location.search).get("onboarding");
  return value === "force" || value === "reset" ? value : null;
}

export type OnboardingGatePhase =
  /** Async sources (tabs, subscription, onboarding pref) still settling. */
  | "waiting"
  /** Not the target audience — never show onboarding. */
  | "ineligible"
  /** First ever login: show the welcome flow from the start. */
  | "trigger"
  /** Flow started but not finished/dismissed: show it at lastStep. */
  | "resume"
  /** Completed, dismissed, or an established user. */
  | "done";

/**
 * Who gets the welcome flow. v1: external trialists only. Isolated so widening
 * the audience later (all new subscribers, demo users…) is a one-line change.
 */
export function isOnboardingEligible(access: AccessControl): boolean {
  if (access.isInternalUser) return false;
  return access.subscriptionQuery.data?.status === "in_trial";
}

/**
 * Derives the onboarding trigger state. Pure derivation — rendering decisions
 * live in OnboardingGate. Also kicks off the onboarding-state fetch once
 * authenticated.
 */
export function useOnboardingGate(): {
  phase: OnboardingGatePhase;
  /** Checklist is shown for eligible users who have begun/finished the flow. */
  showChecklist: boolean;
} {
  const access = useAccessControl();
  const { tabsLoaded, serverTabsEmpty } = useTabStore(
    (s) => ({ tabsLoaded: s.tabsLoaded, serverTabsEmpty: s.serverTabsEmpty }),
    shallow
  );
  const { state, loaded, loadFailed, load } = useOnboardingStore(
    (s) => ({
      state: s.state,
      loaded: s.loaded,
      loadFailed: s.loadFailed,
      load: s.load,
    }),
    shallow
  );

  // Fetch onboarding state once the user is authenticated.
  useEffect(() => {
    if (access.authenticated) {
      load();
    }
  }, [access.authenticated, load]);

  // "force"/"reset" bypass eligibility + empty-tabs on non-production builds.
  const devForce = getOnboardingDevOverride() !== null;

  let phase: OnboardingGatePhase;
  if (!access.authenticated) {
    phase = "ineligible";
  } else if (
    !tabsLoaded ||
    !loaded ||
    access.subscriptionQuery.isLoading
  ) {
    // Wait for ALL async sources before deciding — prevents flashing the
    // welcome modal at users who turn out to be ineligible.
    phase = "waiting";
  } else if (loadFailed || (!devForce && !isOnboardingEligible(access))) {
    phase = "ineligible";
  } else if (state && !state.completedAt && !state.dismissedAt) {
    phase = "resume";
  } else if (!state && (serverTabsEmpty || devForce)) {
    phase = "trigger";
  } else {
    // Completed/dismissed, or an established user (has tabs, no state) —
    // deliberately don't disturb users who already built a dashboard.
    phase = "done";
  }

  const showChecklist =
    phase === "done" &&
    !!state &&
    !state.checklist?.dismissedAt &&
    (devForce || isOnboardingEligible(access));

  return { phase, showChecklist };
}
